import { useApplicationState } from '@modules/state/state_hook';
import { PopoverController } from '@routes/appointment/popover.controller';
import { useService } from '@visiba/instantiation';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { cloneElement, Fragment } from 'react';
import styled from 'styled-components';

interface Props {}

const elements = {
	container: styled(motion.div)`
		position: absolute;
		top: 0;
		right: 0;
		left: 0;
		bottom: 0;
		z-index: 9999;
		overflow: hidden;
	`,
	outer: styled.div<{ overlay: boolean }>`
		position: absolute;
		top: 0;
		right: 0;
		left: 0;
		bottom: 0;
		z-index: -1;
		background: ${({ overlay }) => (overlay ? 'rgba(0, 0, 0, 0.5)' : null)};
	`,
	inner: styled(motion.div)`
		height: 100%;
		width: 100%;
		pointer-events: none;
		display: flex;
		justify-content: center;
		align-items: center;

		h2 {
			text-align: center;
		}
		p {
			margin-bottom: 40px;
		}

		.image {
			margin: 15px 0 25px;
			justify-content: center;
			display: flex;
			svg {
				font-size: 104px;
			}
		}

		.inner {
			display: flex;
			max-height: 100%;
			overflow-y: auto;
			flex-direction: column;
			pointer-events: all;
		}
	`,
};

const variantsWrapper: Variants = {
	enter: {
		opacity: 0,
	},
	center: {
		opacity: 1,
	},
	exit: {
		opacity: 0,
	},
};

const variantsInner: Variants = {
	enter: {
		opacity: 0,
		translateY: 16,
		transition: {
			ease: 'easeInOut',
		},
	},
	center: {
		opacity: 1,
		translateY: 0,
		transition: {
			ease: 'easeInOut',
		},
	},
	exit: {
		opacity: 0,
		translateY: 10,
		transition: {
			ease: 'easeInOut',
		},
	},
};

// Stores a copy of the last element so the exit animation can be used.
let preservedChild: JSX.Element | null = null;

function createModalChild(jsx: JSX.Element): JSX.Element {
	preservedChild = cloneElement(jsx);

	// returns new copy or old copy.
	return preservedChild;
}

function Modal(): JSX.Element {
	const popoverController = useService(PopoverController);
	const [state] = useApplicationState(popoverController.popoverService.modalStack.state);

	const overlay = popoverController.popoverService.currentActiveModal
		? state[popoverController.popoverService.currentActiveModal].overlay
		: false;

	const animations = popoverController.popoverService.currentActiveModal
		? state[popoverController.popoverService.currentActiveModal].animations
		: false;

	function handleClick(): void {
		const modal = popoverController.popoverService.currentActiveModal
			? state[popoverController.popoverService.currentActiveModal]
			: null;

		if (modal && !modal.persistent) {
			popoverController.shift();
		}
	}

	const animationProps = animations
		? {
				initial: 'enter',
				animate: 'center',
				exit: 'exit',
				transition: {
					duration: 0.24,
				},
			}
		: {};

	return (
		<AnimatePresence initial={false}>
			{popoverController.popoverService.currentActiveModal !== null && (
				<elements.container variants={variantsWrapper} {...animationProps}>
					<elements.outer overlay={overlay} onClick={handleClick} />
					<elements.inner variants={variantsInner} {...animationProps}>
						<div className='inner'>{createModalChild(state[popoverController.popoverService.currentActiveModal].jsx)}</div>
					</elements.inner>
				</elements.container>
			)}
		</AnimatePresence>
	);
}

export function PopoverManager(_: Props): JSX.Element {
	return (
		<Fragment>
			<Modal />
		</Fragment>
	);
}
